import { useMemo, useState, type ChangeEvent } from 'react';

/**
 * حقلُ التاريخ بقوائمَ منفصلة: يوم · شهر · سنة (وساعة · دقيقة للموعد) — 23 سبتمبر 2026.
 *
 * لماذا لا `<input type="date">`: كروم يفرض على حقول التاريخ اتجاهاً من اليسار إلى اليمين،
 * فحين تكون لغةُ الجهاز عربيةً يظهر نصُّ الخانات الفارغة مقلوبَ الحروف («هنس/رهش/موي»
 * بدل «سنة/شهر/يوم»)، ويختلف شكلُه بين متصفّحٍ وآخر. القوائمُ عربيةٌ من اليمين، بأسماء الأشهر
 * المعتادة، وتعمل على كل جهاز بالشكل نفسِه.
 *
 * العقدُ كعقد الحقل الأصلي: القيمةُ `YYYY-MM-DD` (أو `YYYY-MM-DDTHH:mm` للموعد، `YYYY-MM` للشهر)،
 * و`onChange` يستلم `{ target: { value } }`، وقيمةٌ ناقصة أو خارج `min/max` تُسلَّم فارغة.
 */
import { MONTHS_AR, composeParts, daysIn, pad, parseParts, type DatePartsKind, type Parts } from './date-parts-util';

export type { DatePartsKind } from './date-parts-util';

interface Props {
  kind: DatePartsKind;
  id?: string;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  /** تاريخُ ميلاد: السنواتُ من الأحدث إلى الأقدم ومداها أوسع إلى الوراء */
  past?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export function DateParts({ kind, id, value, onChange, min, max, required, disabled, name, past, ...aria }: Props) {
  const [p, setP] = useState<Parts>(() => parseParts(kind, value));
  const [rangeErr, setRangeErr] = useState<string | null>(null);
  // قيمةٌ تتغيّر من الخارج (تعبئة، تفريغ النموذج) تعيد القوائم؛ وما أرسله الحقلُ نفسُه لا يعيدها —
  // فاختيارٌ ناقصٌ أو خارج المدى (يُرسَل فارغاً) يبقى ظاهراً ليُصحَّح. تعديلُ الحالة أثناء الرسم لا في أثر.
  const [seen, setSeen] = useState(value ?? '');
  const [emitted, setEmitted] = useState(value ?? '');
  const v = value ?? '';
  if (v !== seen) {
    setSeen(v);
    if (v !== emitted) { setP(parseParts(kind, v)); setRangeErr(null); setEmitted(v); }
  }

  const nowY = new Date().getFullYear();
  const years = useMemo(() => {
    const lo = min ? Number(min.slice(0, 4)) : past ? nowY - 90 : nowY - 10;
    const hi = max ? Number(max.slice(0, 4)) : past ? nowY : nowY + 5;
    const list: number[] = [];
    for (let y = Math.min(lo, hi); y <= Math.max(lo, hi); y++) list.push(y);
    if (p.y && !list.includes(Number(p.y))) list.push(Number(p.y));
    list.sort((a, b) => (past ? b - a : a - b));
    return list;
  }, [min, max, past, nowY, p.y]);

  const minutes = useMemo(() => {
    const list = Array.from({ length: 12 }, (_, i) => pad(i * 5));
    if (p.mi && !list.includes(p.mi)) list.push(p.mi);
    return list.sort();
  }, [p.mi]);

  const update = (patch: Partial<Parts>) => {
    const next = { ...p, ...patch };
    // يومٌ لا يوجد في الشهر الجديد (٣١ ثم فبراير) يُقصّ إلى آخر يومٍ فيه
    if (next.d && Number(next.d) > daysIn(next.y, next.m)) next.d = pad(daysIn(next.y, next.m));
    setP(next);
    let out = composeParts(kind, next);
    if (out && ((min && out < min.slice(0, out.length)) || (max && out > max.slice(0, out.length)))) {
      setRangeErr(min && out < min.slice(0, out.length) ? 'التاريخ قبل أقرب تاريخٍ مسموح' : 'التاريخ بعد آخر تاريخٍ مسموح');
      out = '';
    } else setRangeErr(null);
    setEmitted(out);
    onChange?.({ target: { value: out, name }, currentTarget: { value: out, name } } as unknown as ChangeEvent<HTMLInputElement>);
  };

  const invalid = aria['aria-invalid'] || !!rangeErr;
  const sel = (key: keyof Parts, label: string, options: Array<[string, string]>, cls = '', first = false) => (
    <select
      id={first ? id : undefined} className={`select date-parts__${key} ${cls}`.trim()} aria-label={label}
      value={p[key]} onChange={(e) => update({ [key]: e.target.value })}
      required={required} disabled={disabled} aria-invalid={invalid || undefined}
      aria-describedby={aria['aria-describedby']}
    >
      <option value="">{label}</option>
      {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
    </select>
  );

  const dayCount = daysIn(p.y, p.m);
  return (
    <div className="date-parts-wrap">
      <div className="date-parts" role="group">
        {kind !== 'month' && sel('d', 'اليوم', Array.from({ length: dayCount }, (_, i) => [pad(i + 1), String(i + 1)]), '', true)}
        {sel('m', 'الشهر', MONTHS_AR.map((t, i) => [pad(i + 1), `${t} (${i + 1})`]), '', kind === 'month')}
        {sel('y', 'السنة', years.map((y) => [String(y), String(y)]))}
        {kind === 'datetime-local' && (
          <>
            {sel('h', 'الساعة', Array.from({ length: 24 }, (_, i) => [pad(i), pad(i)]), 'date-parts__time')}
            {sel('mi', 'الدقيقة', minutes.map((m) => [m, m]), 'date-parts__time')}
          </>
        )}
      </div>
      {name && <input type="hidden" name={name} value={composeParts(kind, p)} />}
      {rangeErr && <span className="field__error" role="alert">{rangeErr}</span>}
    </div>
  );
}
